import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { EmptyState } from "@/shared/ui/emptyState/public";
import styles from "./ResourceBrowserLayout.module.css";

export type ResourceBrowserLayoutProps = {
  actions?: ReactNode;
  ariaLabel: string;
  children: ReactNode;
  // 목록 조회 실패의 사유다. 띠가 필터와 데이터 뷰 위에 선다.
  error?: ReactNode;
  // 목록을 다시 받는다. 주면 실패 띠에 `다시 시도` 를 둔다. 다시 받아도 사라지지 않는 오류(동작 실패)에는
  // 주지 않는다.
  onRetry?: () => void;
  filters?: ReactNode;
  // 레이아웃 밖(목록 상단 BulkActionBar 띠)이 결과 수를 소유하는 화면은 true 를 준다. 그러면
  // 툴바에서 결과 수(수+라벨)를 빼고 filters 슬롯의 카드 크롬도 없앤다(무늬 없음). 값을 주지 않으면
  // 결과 수를 툴바에 두고 필터를 카드로 표시한다. admin·업로드처럼 자체 필터 슬롯을 쓰는 화면이 이 경우다.
  summaryInFilter?: boolean;
  footer?: ReactNode;
  notice?: ReactNode;
  summaryCount: number;
  summaryLabel: ReactNode;
};

// 목록 브라우징 화면의 공용 셸이다: 요약/액션 툴바 + notice/error + 필터 슬롯 + 데이터 뷰 + footer.
// 특정 도메인에 의존하지 않고, 화면별 내용은 슬롯으로 받는다. 검색 슬롯은 없다. 조회 축은
// 필터 바(예: `FilterBar`)가 한 단위로 소유한다(rules.md §17).
export function ResourceBrowserLayout({
  actions = null,
  ariaLabel,
  children,
  error = null,
  onRetry,
  filters = null,
  summaryInFilter = false,
  footer = null,
  notice = null,
  summaryCount,
  summaryLabel,
}: ResourceBrowserLayoutProps): ReactNode {
  // 밖(BulkActionBar 띠)이 결과 수를 소유하는 모드다. 툴바에서 결과 수를 뺀다.
  const filterOwnsSummary = summaryInFilter;
  // 요약·액션이 모두 밖(페이지 belowTabs·목록 상단 띠)에 있는 화면은 툴바 행을 그리지 않는다.
  // 그래서 빈 행이 필터와 본문 사이에 공백으로 남지 않는다.
  const showToolbar = !filterOwnsSummary || actions != null;

  // 결과 수(수+라벨)다. filterOwnsSummary 가 아니면 툴바에 렌더한다.
  const summary = (
    <div className={styles.summary}>
      <Text variant={hatcheryTextModes["title-bold"]} className={styles.count}>
        {summaryCount}
      </Text>
      <Text variant={hatcheryTextModes["body-bold"]}>{summaryLabel}</Text>
    </div>
  );

  return (
    <section className={styles.shell} aria-label={ariaLabel}>
      {showToolbar ? (
        <div className={styles.toolbar}>
          <div className={styles.toolbarContent}>{filterOwnsSummary ? null : summary}</div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </div>
      ) : null}

      {notice ? (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.notice}>
          {notice}
        </Text>
      ) : null}

      {error ? (
        <div className={styles.error}>
          <EmptyState
            size="sm"
            tone="error"
            action={onRetry === undefined ? undefined : { label: "다시 시도", onClick: onRetry }}
          >
            {error}
          </EmptyState>
        </div>
      ) : null}

      {filters ? (
        <div className={[styles.filters, filterOwnsSummary ? styles.filtersBare : ""].join(" ")}>
          {filters}
        </div>
      ) : null}

      <div className={styles.dataView}>{children}</div>

      {footer ? <div className={styles.footer}>{footer}</div> : null}
    </section>
  );
}
